import {getAgentsSpacesPath} from '@github-ui/agent-sessions/utils/constants'
import {
  ConversationFeedbackDialog,
  type FeedbackDialogRef,
} from '@github-ui/copilot-chat/components/ConversationFeedbackDialog'
import {useSafeChatStateLens} from '@github-ui/copilot-chat/CopilotChatContext'
import type {CopilotSpace, CopilotSpaceId} from '@github-ui/copilot-chat-types'
import {ReportSpaceDialog} from '@github-ui/copilot-spaces/components/ReportSpaceDialog'
import {DeleteSpaceDialog} from '@github-ui/copilot-spaces/components/SpaceMenu'
import {SpaceSharingDialog} from '@github-ui/copilot-spaces/components/SpaceSharingDialog'
import {useReportUrl} from '@github-ui/copilot-spaces/hooks'
import {useDeleteCopilotSpace} from '@github-ui/copilot-spaces/hooks/use-delete-copilot-space-no-chat-state'
import {useStarCopilotSpace} from '@github-ui/copilot-spaces/hooks/use-star-copilot-space-no-chat-state'
import {sendEvent} from '@github-ui/hydro-analytics'
import {Breadcrumbs} from '@github-ui/pacer/Breadcrumbs'
import {useNavigate} from '@github-ui/use-navigate'
import {
  AlertIcon,
  BookIcon,
  CopyIcon,
  KebabHorizontalIcon,
  ReportIcon,
  ShareIcon,
  StarFillIcon,
  StarIcon,
  TrashIcon,
} from '@primer/octicons-react'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {ActionList, ActionMenu, Button, Flash, IconButton} from '@primer/react'
import {useRef, useState} from 'react'

import {AgentsCreateSpaceDialog} from './AgentsCreateSpaceDialog'
import styles from './AgentsSpaceHeader.module.css'

const DOCS_URL =
  'https://docs.github.com/copilot/how-tos/provide-context/use-copilot-spaces/create-and-use-copilot-spaces'

/** Copy of the legacy /copilot/spaces SpacesHeader top bar, without the chat-thread parts. */
export function AgentsSpaceHeader({copilot}: {copilot: CopilotSpace}) {
  const optedInToUserFeedback = useSafeChatStateLens(state => state?.optedInToUserFeedback)
  const navigate = useNavigate()
  const [showDeleteSpaceDialog, setShowDeleteSpaceDialog] = useState(false)
  const [showShareSpaceDialog, setShowShareSpaceDialog] = useState(false)
  const [showDuplicateSpaceDialog, setShowDuplicateSpaceDialog] = useState(false)
  const [showReportSpaceDialog, setShowReportSpaceDialog] = useState(false)
  const [deletionError, setDeletionError] = useState<Error | null>(null)
  const shareButtonRef = useRef<HTMLButtonElement>(null)
  const breadcrumbMenuRef = useRef<HTMLButtonElement>(null)
  const feedbackRef = useRef<FeedbackDialogRef>(null)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const {mutateAsync: deleteCopilotSpace} = useDeleteCopilotSpace()
  const spaceId: CopilotSpaceId = {id: copilot.id, owner: copilot.owner}

  const sharingEnabled = copilot.adminable

  // Show report button for spaces that are not owned by the current user and are public/shared
  const isOrgPublicOrShared = copilot.ownerIsOrg && (copilot.baseRole !== 'none' || copilot.collaborative)
  const reportingEnabled = Boolean(isOrgPublicOrShared || copilot.shared)
  const reportUrl = useReportUrl(spaceId, getAgentsSpacesPath())

  const {starCopilotSpace, isLoading} = useStarCopilotSpace(spaceId)

  const closeDuplicateSpaceDialog = () => {
    sendEvent('dotcom_chat.activate', {target: 'COPILOT_SPACE_DUPLICATE_DIALOG_CLOSE', mode: 'immersive'})
    setShowDuplicateSpaceDialog(false)
  }

  const closeShareSpaceDialog = () => {
    sendEvent('dotcom_chat.activate', {target: 'COPILOT_SPACE_SHARE_DIALOG_CLOSE', mode: 'immersive'})
    setShowShareSpaceDialog(false)
  }

  const closeDeleteSpaceDialog = () => {
    sendEvent('dotcom_chat.activate', {target: 'COPILOT_SPACE_DELETE_DIALOG_CLOSE', mode: 'immersive'})
    setShowDeleteSpaceDialog(false)
  }

  const closeReportSpaceDialog = () => {
    sendEvent('dotcom_chat.activate', {target: 'COPILOT_SPACE_REPORT_DIALOG_CLOSE', mode: 'immersive'})
    setShowReportSpaceDialog(false)
  }

  const openDuplicateSpaceDialog = () => {
    sendEvent('dotcom_chat.activate', {target: 'COPILOT_SPACE_CONTEXT_MENU_DUPLICATE', mode: 'immersive'})
    setShowDuplicateSpaceDialog(true)
  }

  const openShareSpaceDialog = () => {
    sendEvent('dotcom_chat.activate', {target: 'COPILOT_SPACE_CONTEXT_MENU_SHARE', mode: 'immersive'})
    setShowShareSpaceDialog(true)
  }

  const openDeleteSpaceDialog = () => {
    sendEvent('dotcom_chat.activate', {target: 'COPILOT_SPACE_CONTEXT_MENU_DELETE', mode: 'immersive'})
    setShowDeleteSpaceDialog(true)
  }

  const openReportSpaceDialog = () => {
    sendEvent('dotcom_chat.activate', {target: 'COPILOT_SPACE_CONTEXT_MENU_REPORT', mode: 'immersive'})
    setShowReportSpaceDialog(true)
  }

  const deleteSpace = async () => {
    try {
      await deleteCopilotSpace(spaceId)
      setShowDeleteSpaceDialog(false)
      navigate(getAgentsSpacesPath())
    } catch (err) {
      setDeletionError(err instanceof Error ? err : new Error(String(err)))
      closeDeleteSpaceDialog()
    }
  }

  const handleStarClick = () => {
    sendEvent('dotcom_chat.activate', {
      target: copilot.starred ? 'SPACE_UNSTAR' : 'SPACE_STAR',
      mode: 'immersive',
    })
    void starCopilotSpace({starred: !copilot.starred})
  }

  const handleFeedbackClick = () => {
    feedbackRef.current?.openDialog()
    sendEvent('dotcom_chat.activate', {
      target: 'USER_MENU_GIVE_FEEDBACK',
      mode: 'immersive',
    })
  }

  const handleDocsClick = () => {
    sendEvent('copilot-spaces.click', {target: 'HELP_BUTTON_CLICKED'})
  }

  const customButtonsContent = (
    <>
      {optedInToUserFeedback && (
        <Button variant="link" onClick={handleFeedbackClick}>
          Give feedback
        </Button>
      )}
      <IconButton
        icon={BookIcon}
        as="a"
        href={DOCS_URL}
        aria-label="Copilot Spaces documentation"
        target="_blank"
        rel="noopener noreferrer"
        variant="invisible"
        onClick={handleDocsClick}
      />
      {sharingEnabled && (
        <IconButton
          ref={shareButtonRef}
          icon={ShareIcon}
          onClick={openShareSpaceDialog}
          variant="invisible"
          aria-label="Share space"
        />
      )}
      {reportingEnabled && (
        <IconButton
          icon={ReportIcon}
          onClick={openReportSpaceDialog}
          variant="invisible"
          aria-label="Report space content"
        />
      )}
      <IconButton
        onClick={handleStarClick}
        disabled={isLoading}
        icon={copilot.starred ? StarFillIcon : StarIcon}
        className={copilot.starred ? styles.starButton : undefined}
        variant="invisible"
        aria-label={copilot.starred ? 'Unstar space' : 'Star space'}
      />
    </>
  )

  const customButtonsMenu = (
    <>
      <ActionList.LinkItem href={DOCS_URL} target="_blank" rel="noopener noreferrer" onSelect={handleDocsClick}>
        <ActionList.LeadingVisual>
          <BookIcon />
        </ActionList.LeadingVisual>
        Copilot Spaces documentation
      </ActionList.LinkItem>
      {sharingEnabled && (
        <ActionList.Item onSelect={openShareSpaceDialog}>
          <ActionList.LeadingVisual>
            <ShareIcon />
          </ActionList.LeadingVisual>
          Share space
        </ActionList.Item>
      )}
      {reportingEnabled && (
        <ActionList.Item onSelect={openReportSpaceDialog}>
          <ActionList.LeadingVisual>
            <ReportIcon />
          </ActionList.LeadingVisual>
          Report space content
        </ActionList.Item>
      )}
      <ActionList.Item onSelect={handleStarClick} disabled={isLoading}>
        <ActionList.LeadingVisual>{copilot.starred ? <StarFillIcon /> : <StarIcon />}</ActionList.LeadingVisual>
        <span>{copilot.starred ? 'Unstar space' : 'Star space'}</span>
      </ActionList.Item>
      <ActionList.Divider />
      <ActionList.Item onSelect={handleFeedbackClick}>Give feedback</ActionList.Item>
    </>
  )

  return (
    <>
      <div className={styles.container}>
        <div className={styles.leftControls}>
          <Breadcrumbs>
            <Breadcrumbs.Item href={getAgentsSpacesPath()}>Spaces</Breadcrumbs.Item>
            <Breadcrumbs.ItemWithMenu
              buttonRef={breadcrumbMenuRef}
              menuOverlay={
                <ActionMenu.Overlay>
                  <ActionList>
                    <ActionList.Item onSelect={openDuplicateSpaceDialog}>
                      <ActionList.LeadingVisual>
                        <CopyIcon />
                      </ActionList.LeadingVisual>
                      Duplicate
                    </ActionList.Item>
                    {copilot.adminable && (
                      <ActionList.Item variant="danger" onSelect={openDeleteSpaceDialog}>
                        <ActionList.LeadingVisual>
                          <TrashIcon />
                        </ActionList.LeadingVisual>
                        Delete
                      </ActionList.Item>
                    )}
                  </ActionList>
                </ActionMenu.Overlay>
              }
            >
              {copilot.name}
            </Breadcrumbs.ItemWithMenu>
          </Breadcrumbs>
        </div>
        <div className={styles.rightControls}>
          <div className={styles.hideOnMobile}>{customButtonsContent}</div>
          <div className={styles.showOnMobile}>
            <ActionMenu>
              <ActionMenu.Anchor>
                <IconButton icon={KebabHorizontalIcon} aria-label="Space options" variant="invisible" />
              </ActionMenu.Anchor>
              <ActionMenu.Overlay>
                <ActionList>{customButtonsMenu}</ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          </div>
        </div>
      </div>
      {deletionError && (
        <Flash variant="danger">
          <AlertIcon />
          There was a problem deleting your space.
        </Flash>
      )}

      {showDuplicateSpaceDialog && (
        <AgentsCreateSpaceDialog
          dialogTitle="Duplicate space"
          closeDialog={closeDuplicateSpaceDialog}
          initialSpace={copilot}
          returnFocusRef={breadcrumbMenuRef}
          spacesBasePath={getAgentsSpacesPath()}
        />
      )}
      {showDeleteSpaceDialog && (
        <DeleteSpaceDialog
          onCancel={closeDeleteSpaceDialog}
          onConfirm={deleteSpace}
          returnFocusRef={breadcrumbMenuRef}
        />
      )}
      {showShareSpaceDialog && (
        <SpaceSharingDialog
          closeDialog={closeShareSpaceDialog}
          customCopilotId={spaceId}
          userOwned={!copilot.ownerIsOrg}
          returnFocusRef={shareButtonRef}
        />
      )}
      {showReportSpaceDialog && <ReportSpaceDialog reportUrl={reportUrl} onClose={closeReportSpaceDialog} />}

      <ConversationFeedbackDialog ref={feedbackRef} mode="immersive" returnFocusRef={anchorRef} />
    </>
  )
}
